import React, { Fragment, useCallback, useEffect, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { Container } from "reactstrap";
import { ArrowLeft } from "react-feather";
import Breadcrumb from "../../common/breadcrumb";
import { C, reportingGlobalCSS } from "../../reporting/reportingStyles";
import { Badge, Loader, RCard } from "../../reporting/ReportingShared";
import { Field, SaveBar, inputStyle, primaryBtnStyle, secondaryBtnStyle, withDisabled } from "../../common/FormKit";
import { errorToast, successToast } from "../../../shared/Toast";
import { getMerchantByID } from "../../../utils/apiCalls";
import {
  activatePosReceiptTemplate, duplicatePosReceiptTemplate, getPosReceiptTemplate, updatePosReceiptTemplate,
} from "../../../utils/posReceiptTemplateApi";
import PosReceiptTemplateEditor from "./PosReceiptTemplateEditor";
import PosReceiptPreview from "./PosReceiptPreview";
import { LIMITS, findLayoutProblems } from "./posReceiptTemplateMeta";
import useSelectedMerchant from "./useSelectedMerchant";

/**
 * Edit one template (or preview a system one read-only). Saves name + layout through
 * PUT /api/pos-receipt-templates/:id; the server re-validates and answers 422 with every problem.
 */
const PosReceiptTemplateEditorPage = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const { merchantId, hasMerchant, isAdmin, readOnly: roleReadOnly } = useSelectedMerchant();

  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [template, setTemplate] = useState(null);
  const [name, setName] = useState("");
  const [layout, setLayout] = useState(null);
  const [dirty, setDirty] = useState(false);
  const [logoUrl, setLogoUrl] = useState(null);
  const [company, setCompany] = useState(null);

  const readOnly = roleReadOnly || (template?.isSystem && !isAdmin);

  const load = useCallback(async () => {
    setLoading(true);
    const t = await getPosReceiptTemplate(id);
    setTemplate(t);
    setName(t?.name || "");
    setLayout(t?.layout || null);
    setDirty(false);
    setLoading(false);
  }, [id]);

  useEffect(() => { load(); }, [load]);

  // Only for the preview; the page works without it.
  useEffect(() => {
    if (!hasMerchant) return;
    getMerchantByID(merchantId)
      .then((res) => {
        const m = res?.data;
        if (m?.receiptLogo) setLogoUrl(process.env.REACT_APP_URL + m.receiptLogo);
        if (m?.name) setCompany({ brandName: m.name, ...(m.arabicName ? { arabicName: m.arabicName } : {}) });
      })
      .catch(() => {});
  }, [hasMerchant, merchantId]);

  const onLayoutChange = (next) => { setLayout(next); setDirty(true); };
  const onNameChange = (v) => { setName(v); setDirty(true); };

  const save = async () => {
    const problems = findLayoutProblems(layout);
    if (!name.trim()) problems.unshift("Give the template a name");
    if (problems.length) { errorToast(problems.join(" · ")); return; }
    setSaving(true);
    const res = await updatePosReceiptTemplate(template.id, { name: name.trim(), layout });
    setSaving(false);
    if (res) {
      successToast(template.isSystem ? "System template saved for every merchant using it" : "Template saved. POS devices pick it up on their next config refresh.");
      setTemplate(res.data);
      setLayout(res.data.layout);
      setName(res.data.name);
      setDirty(false);
    }
  };

  const clone = async () => {
    setSaving(true);
    const res = await duplicatePosReceiptTemplate(template.id, { merchantId });
    setSaving(false);
    if (res?.data?.id) {
      successToast("Template cloned — you are editing the copy");
      navigate(`/merchant/pos-receipt-templates/${res.data.id}`);
    }
  };

  const activate = async () => {
    setSaving(true);
    const res = await activatePosReceiptTemplate(template.id, merchantId);
    setSaving(false);
    if (res) successToast(res.message || "Template is now active");
  };

  const back = () => navigate("/merchant/pos-receipt-templates");

  const body = () => {
    if (loading) return <RCard><Loader /></RCard>;
    if (!template || !layout) {
      return <RCard><p style={{ textAlign: "center", padding: 40, color: C.textLight, margin: 0 }}>Template not found.</p></RCard>;
    }
    return (
      <div style={{ display: "grid", gridTemplateColumns: "minmax(0,1fr) 380px", gap: 20, alignItems: "start" }}>
        <div>
          <RCard title={template.isSystem ? "🧾 System template" : "🧾 Template"}>
            <div style={{ marginBottom: 14 }}>
              <Field label="Name" required>
                <input type="text" value={name} maxLength={LIMITS.name} disabled={readOnly} style={inputStyle} onChange={(e) => onNameChange(e.target.value)} aria-label="Template name" />
              </Field>
            </div>
            <PosReceiptTemplateEditor value={layout} onChange={onLayoutChange} readOnly={readOnly} merchantId={merchantId} />
          </RCard>
          {!readOnly && (
            <SaveBar dirty={dirty}>
              <button type="button" style={withDisabled(secondaryBtnStyle, saving || !dirty)} disabled={saving || !dirty} onClick={load}>Discard</button>
              <button type="button" style={withDisabled(primaryBtnStyle, saving || !dirty)} disabled={saving || !dirty} onClick={save}>{saving ? "Saving…" : "Save"}</button>
            </SaveBar>
          )}
        </div>
        <div style={{ position: "sticky", top: 90 }}>
          <RCard title="👁️ Preview">
            <PosReceiptPreview layout={layout} logoUrl={logoUrl} company={company} />
          </RCard>
        </div>
      </div>
    );
  };

  return (
    <Fragment>
      <style>{reportingGlobalCSS}</style>
      <Breadcrumb title="POS Receipt Template" parent="Merchants" />
      <Container fluid className="rpt-wrap">
        <div style={{ display: "flex", gap: 12, alignItems: "center", marginBottom: 20, flexWrap: "wrap" }}>
          <button type="button" style={secondaryBtnStyle} onClick={back}><ArrowLeft size={14} /> Templates</button>
          <div>
            <div style={{ fontWeight: 700, fontSize: 15, color: C.text }}>{template?.name || "Template"}</div>
            <div style={{ fontSize: 12, color: C.textLight }}>
              {template?.isSystem
                ? (isAdmin ? "A system template: changes apply to every merchant printing with it." : "A system template. Clone it to make changes.")
                : "Changes reach the POS on its next config refresh (open Settings on the device)."}
            </div>
          </div>
          <div style={{ flex: 1 }} />
          {template && (
            <>
              {dirty && <Badge color={C.orange} bg={C.orangeLight}>Unsaved changes</Badge>}
              {readOnly && <Badge color={C.textMed} bg={C.bg}>Read only</Badge>}
              {!roleReadOnly && hasMerchant && (
                <>
                  <button type="button" style={withDisabled(secondaryBtnStyle, saving)} disabled={saving} onClick={clone}>Clone</button>
                  <button type="button" style={withDisabled(secondaryBtnStyle, saving || dirty)} disabled={saving || dirty} title={dirty ? "Save first" : ""} onClick={activate}>Set active</button>
                </>
              )}
            </>
          )}
        </div>
        {body()}
      </Container>
    </Fragment>
  );
};

export default PosReceiptTemplateEditorPage;
